import { without } from 'lodash-es';
import { YesOrNo } from './common.model';
import { Order } from './order.model';

export enum AddressType {
  Shipping = 'shipping',
  Billing = 'billing',
  Correspondence = 'letter',
}

// API Models

export interface ApiPatientAddressItemResponse {
  addressId?: number;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zip?: string;
  addressTypes: AddressType[];
  attention?: string;
  addressVerified?: YesOrNo;
  carrierRoute?: string;
}

export interface ApiPatientAddressesResponse {
  addresses: ApiPatientAddressItemResponse[];
}

export interface ApiPatientAddressesRequest {
  patientId: number;
  addresses: ApiPatientAddressItemResponse[];
}

export interface ApiAddressOrdersResponse {
  patientId: number;
  addressId: number;
  reorders: number[];
  shipments: number[];
}

export interface ApiAddressOrdersRequest extends ApiAddressOrdersResponse {}

// Front End Models

export interface Address {
  id?: number;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  attention?: string | null;
}

export interface PatientAddress extends Address {
  id: number;
  types: AddressType[];
  addressVerified: boolean;
  carrierRoute: string | null;
}

export interface AddressOrders {
  addressId: number;
  orders: Order[];
}

// Form Models

export interface PatientAddressFormValues extends Address {
  types: AddressType[];
  addressVerified: boolean;
  carrierRoute: string | null;
}

export interface AddressOrdersFormItem {
  orderId: number;
  updateAddress: boolean;
}

export interface AddressOrdersFormValues {
  orderUpdates: AddressOrdersFormItem[];
}

// Mappers

export const mapAddress = (
  id?: number | null,
  line1?: string | null,
  line2?: string | null,
  city?: string | null,
  state?: string | null,
  zipCode?: string | null,
  attention: string | null = null
): Address | undefined => {
  if (line1 || line2 || city || state || zipCode || attention) {
    let formattedZipCode = zipCode ? zipCode.replace(/\s/g, '') : null;

    if (formattedZipCode && formattedZipCode.length > 5 && formattedZipCode.charAt(5) !== '-') {
      formattedZipCode = formattedZipCode.substring(0, 5) + '-' + formattedZipCode.substring(5);
    }

    return {
      id: id || undefined,
      line1: line1 || null,
      line2: line2 || null,
      city: city || null,
      state: state || null,
      zipCode: formattedZipCode,
      attention: attention || null,
    };
  }

  return undefined;
};

export const mapPatientAddresses = (patientAddresses: ApiPatientAddressesResponse): PatientAddress[] => {
  return patientAddresses.addresses.map((pa) => ({
    types: pa.addressTypes,
    addressVerified: pa.addressVerified === YesOrNo.Y,
    carrierRoute: pa.carrierRoute || null,
    ...mapAddress(pa.addressId || -1, pa.line1, pa.line2, pa.city, pa.state, pa.zip, pa.attention),
    id: pa.addressId || -1, // For typescript
  }));
};

export const mapPatientAddressFormValues = (paf: PatientAddressFormValues): PatientAddress => {
  return {
    ...paf,
    id: paf.id || -1,
  };
};

export const mapAddressOrders = (list: ApiAddressOrdersResponse, allOrders: Order[]): AddressOrders => {
  return {
    addressId: list.addressId,
    orders: [
      ...list.reorders.map((r) => allOrders.find((o) => o.id === r)!),
      ...list.shipments.map((s) => allOrders.find((o) => o.id === s)!),
    ].filter((o) => !!o),
  };
};

export const buildPatientAddressesRequest = (
  patientId: number,
  patientAddresses: PatientAddress[],
  newAddress?: PatientAddressFormValues | null
): ApiPatientAddressesRequest => {
  let addresses: ApiPatientAddressItemResponse[] = patientAddresses.map((pa) => {
    return {
      addressId: pa.id,
      line1: pa.line1 || undefined,
      line2: pa.line2 || undefined,
      city: pa.city || undefined,
      state: pa.state || undefined,
      zip: pa.zipCode?.replace(/-/g, '') || undefined,
      addressTypes: pa.types,
      attention: pa.attention || undefined,
      addressVerified: pa.addressVerified ? YesOrNo.Y : YesOrNo.N,
      carrierRoute: pa.carrierRoute || undefined,
    };
  });

  if (newAddress) {
    if (newAddress.types) {
      addresses = addresses.map((pa) => ({
        ...pa,
        addressTypes: without(pa.addressTypes, ...newAddress.types),
      }));
    }

    addresses.push({
      addressId: newAddress.id !== -1 ? newAddress.id : undefined,
      line1: newAddress.line1 || undefined,
      line2: newAddress.line2 || undefined,
      city: newAddress.city || undefined,
      state: newAddress.state || undefined,
      zip: newAddress.zipCode?.replace(/-/g, '') || undefined,
      addressTypes: newAddress.types || undefined,
      attention: newAddress.attention || undefined,
      addressVerified: newAddress.addressVerified ? YesOrNo.Y : YesOrNo.N,
      carrierRoute: newAddress.carrierRoute || undefined,
    } as ApiPatientAddressItemResponse);
  }

  return {
    patientId,
    addresses,
  };
};

export const buildAddressOrdersRequest = (
  patientId: number,
  addressId: number,
  allOrders: Order[],
  formValues: AddressOrdersFormValues
): ApiAddressOrdersRequest => {
  // TODO Add error handling
  return {
    patientId,
    addressId: addressId,
    reorders: formValues.orderUpdates
      .filter((ou) => !allOrders.find((o) => o.id === ou.orderId)?.isShipment && ou.updateAddress)
      .map((ou) => ou.orderId),
    shipments: formValues.orderUpdates
      .filter((ou) => allOrders.find((o) => o.id === ou.orderId)?.isShipment && ou.updateAddress)
      .map((ou) => ou.orderId),
  };
};
